import type { HttpPlugin } from "@kalodata/http";
import { reportApiError } from "@kalodata/sentry";
import type { ApiErrorInput } from "@kalodata/sentry";
import type { ClipHttpMeta, ClipHttpContext } from "./types";

const FILTERED_VALUE = "[Filtered]";
const FORM_DATA_VALUE = "[FormData]";
const CIRCULAR_VALUE = "[Circular]";
const UNSERIALIZABLE_VALUE = "[Unserializable]";

const FULL_REDACT_KEYS = new Set([
  "password",
  "token",
  "accesstoken",
  "refreshtoken",
  "authorization",
  "credential",
  "code",
  "smscode",
  "emailcode",
  "tccode",
  "response",
  "captcha",
]);

const PARTIAL_MASK_KEYS = new Set(["phonenumber", "email"]);

type SentryReporter = (input: ApiErrorInput) => void;

const normalizeKey = (key: string) => key.replace(/[\s_-]/g, "").toLowerCase();

const isFullRedactKey = (key: string) => FULL_REDACT_KEYS.has(normalizeKey(key));

const isPartialMaskKey = (key: string) => PARTIAL_MASK_KEYS.has(normalizeKey(key));

const maskGeneric = (value: string) => {
  if (!value) return value;
  if (value.length <= 2) return `${value.slice(0, 1)}***`;
  if (value.length <= 4) return `${value.slice(0, 1)}***${value.slice(-1)}`;

  const prefixLength = Math.min(2, Math.floor(value.length / 3));
  const suffixLength = Math.min(2, Math.floor(value.length / 3));
  return `${value.slice(0, prefixLength)}***${value.slice(-suffixLength)}`;
};

const maskEmail = (value: string) => {
  const atIndex = value.indexOf("@");
  if (atIndex <= 0) return maskGeneric(value);

  const localPart = value.slice(0, atIndex);
  const domain = value.slice(atIndex);

  if (localPart.length <= 1) return `${localPart.slice(0, 1)}***${domain}`;
  if (localPart.length <= 4) return `${localPart.slice(0, 1)}***${localPart.slice(-1)}${domain}`;

  return `${localPart.slice(0, 2)}***${localPart.slice(-2)}${domain}`;
};

const maskPhone = (value: string) => {
  const digitCount = value.replace(/\D/g, "").length;
  if (digitCount <= 7) return maskGeneric(value);

  let seenDigits = 0;
  return value.replace(/\d/g, (digit) => {
    seenDigits += 1;
    return seenDigits <= 3 || seenDigits > digitCount - 4 ? digit : "*";
  });
};

const maskPartialValue = (key: string, value: string | number) => {
  const stringValue = String(value);
  const normalizedKey = normalizeKey(key);

  if (normalizedKey === "email") return maskEmail(stringValue);
  if (normalizedKey === "phonenumber") return maskPhone(stringValue);

  return maskGeneric(stringValue);
};

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  if (Object.prototype.toString.call(value) !== "[object Object]") return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
};

export const redactSentryPayload = (value: unknown, seen = new WeakSet<object>()): unknown => {
  try {
    if (value === null || value === undefined || typeof value !== "object") return value;

    if (typeof FormData !== "undefined" && value instanceof FormData) {
      return FORM_DATA_VALUE;
    }

    if (seen.has(value)) return CIRCULAR_VALUE;
    seen.add(value);

    if (Array.isArray(value)) {
      try {
        return value.map((item) => redactSentryPayload(item, seen));
      } finally {
        seen.delete(value);
      }
    }

    try {
      if (!isPlainObject(value)) {
        return `[${value.constructor?.name || "Object"}]`;
      }

      return Object.keys(value).reduce<Record<string, unknown>>((result, key) => {
        if (isFullRedactKey(key)) {
          result[key] = FILTERED_VALUE;
          return result;
        }

        if (isPartialMaskKey(key)) {
          const fieldValue = value[key];
          result[key] =
            typeof fieldValue === "string" || typeof fieldValue === "number"
              ? maskPartialValue(key, fieldValue)
              : FILTERED_VALUE;
          return result;
        }

        try {
          result[key] = redactSentryPayload(value[key], seen);
        } catch {
          result[key] = UNSERIALIZABLE_VALUE;
        }

        return result;
      }, {});
    } finally {
      seen.delete(value);
    }
  } catch {
    return UNSERIALIZABLE_VALUE;
  }
};

export const createSentryPlugin = (
  reporter: SentryReporter = reportApiError,
): HttpPlugin<ClipHttpMeta, ClipHttpContext> => {
  return {
    name: "sentry",
    onError: (error, context) => {
      const request = error.request || context.request;
      const responseData = error.response?.data as { code?: string | number; message?: string } | undefined;

      try {
        reporter({
          platform: "kaloclip",
          url: request.url,
          method: request.method,
          status: error.status,
          code: responseData?.code,
          message: error.message,
          traceID: error.traceId,
          requestData: redactSentryPayload(request.body),
          responseData: redactSentryPayload(error.response?.data),
          originalError: error,
        });
      } catch {}

      throw error; // Re-throw to maintain error propagation
    },
  };
};
